"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateDeckSlide = validateDeckSlide;
exports.validateDeck = validateDeck;
const deck_schema_1 = require("./deck-schema");
function zodErrors(error, prefix = '') {
    return error.issues.map((issue) => ({
        path: [prefix, ...issue.path.map(String)].filter(Boolean).join('.'),
        message: issue.message,
    }));
}
function isAbsoluteHttpUrl(value) {
    if (!value)
        return false;
    try {
        const parsed = new URL(value);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    }
    catch {
        return false;
    }
}
const EXTERNAL_SOURCE_KINDS = new Set(['website', 'ad-library', 'review-site', 'search', 'keyword-planner', 'other']);
const STRUCTURAL_LAYOUTS = new Set(['agenda', 'data-needed']);
const FIGURE_FIELDS = new Set([
    'title',
    'subtitle',
    'body',
    'value',
    'detail',
    'need',
    'bio',
    'conclusion',
    'marker',
    'role',
    'items',
    'text',
]);
function isEvidence(value) {
    return !!value && typeof value === 'object' && 'confidence' in value && typeof value.confidence === 'string';
}
function adjacentEvidence(value) {
    if (isEvidence(value.evidence))
        return value.evidence;
    if (value.claim &&
        typeof value.claim === 'object' &&
        'evidence' in value.claim &&
        isEvidence(value.claim.evidence)) {
        return value.claim.evidence;
    }
    return undefined;
}
function containsNumericFigure(value) {
    if (typeof value === 'number')
        return true;
    if (typeof value === 'string')
        return /\p{N}/u.test(value);
    if (Array.isArray(value))
        return value.some(containsNumericFigure);
    return false;
}
function isFactualDisplay(value) {
    if ('kind' in value && 'accessedAt' in value)
        return false;
    if ('number' in value && 'title' in value)
        return false;
    if ('layout' in value)
        return typeof value.layout === 'string' && !STRUCTURAL_LAYOUTS.has(value.layout);
    return [...FIGURE_FIELDS].some((field) => field in value) || ('x' in value && 'y' in value && 'label' in value);
}
function figureFields(value, layout) {
    const fields = [...FIGURE_FIELDS, ...('x' in value && 'y' in value ? ['label'] : [])].filter((field) => field in value);
    // These fixed layout fields carry plan quantities, so they are structural
    // rather than a caller-controlled exemption for arbitrary content.
    if (layout === 'timeline' && 'label' in value && 'title' in value && 'body' in value) {
        return fields.filter((field) => field !== 'body');
    }
    if (layout === 'numbered-columns' && 'title' in value && 'items' in value) {
        return fields.filter((field) => field !== 'items');
    }
    return fields;
}
function figureEvidenceErrors(value, path, layout) {
    if (!isFactualDisplay(value))
        return [];
    const fields = figureFields(value, layout);
    if (!fields.some((field) => containsNumericFigure(value[field])))
        return [];
    const evidence = adjacentEvidence(value);
    if (!evidence) {
        return [
            {
                path: `${path}.evidence`,
                message: 'Displayed numeric figures require adjacent evidence.',
            },
        ];
    }
    if (evidence.confidence === 'unavailable') {
        return [{ path, message: 'Unavailable evidence must not be used to display a numeric figure.' }];
    }
    return [];
}
function evidenceErrors(evidence, path, sourceIds) {
    const errors = [];
    if (sourceIds && 'sourceIds' in evidence) {
        for (const sourceId of evidence.sourceIds ?? []) {
            if (!sourceIds.has(sourceId)) {
                errors.push({ path: `${path}.sourceIds`, message: `Unknown source id "${sourceId}".` });
            }
        }
    }
    return errors;
}
function visitEvidence(value, path, sourceIds, errors, inheritedLayout) {
    if (!value || typeof value !== 'object')
        return false;
    if ('confidence' in value && typeof value.confidence === 'string') {
        const evidence = value;
        errors.push(...evidenceErrors(evidence, path, sourceIds));
        return evidence.confidence === 'unavailable';
    }
    const record = value;
    const layout = typeof record.layout === 'string' ? record.layout : inheritedLayout;
    errors.push(...figureEvidenceErrors(record, path, layout));
    let hasUnavailableEvidence = false;
    for (const [key, nestedValue] of Object.entries(record)) {
        if (key === 'evidence' && nestedValue && typeof nestedValue === 'object') {
            const evidence = nestedValue;
            errors.push(...evidenceErrors(evidence, `${path}.${key}`, sourceIds));
            if (evidence.confidence === 'unavailable') {
                hasUnavailableEvidence = true;
                const figure = 'value' in value ? value.value : undefined;
                const displaysNumber = typeof figure === 'number' || (typeof figure === 'string' && /\p{N}/u.test(figure));
                if (key === 'evidence' && displaysNumber) {
                    errors.push({
                        path: `${path}.value`,
                        message: 'An unavailable figure must not display a numeric value.',
                    });
                }
            }
            continue;
        }
        if (Array.isArray(nestedValue)) {
            nestedValue.forEach((item, index) => {
                const itemHasUnavailableEvidence = visitEvidence(item, `${path}.${key}.${index}`, sourceIds, errors, layout);
                hasUnavailableEvidence || (hasUnavailableEvidence = itemHasUnavailableEvidence);
            });
        }
        else {
            const nestedHasUnavailableEvidence = visitEvidence(nestedValue, `${path}.${key}`, sourceIds, errors, layout);
            hasUnavailableEvidence || (hasUnavailableEvidence = nestedHasUnavailableEvidence);
        }
    }
    return hasUnavailableEvidence;
}
function deckRuleErrors(deck, slidePrefix = 'slides') {
    const errors = [];
    const sourceIds = new Set();
    deck.sources.forEach((source, index) => {
        if (sourceIds.has(source.id)) {
            errors.push({ path: `sources.${index}.id`, message: `Duplicate source id "${source.id}".` });
        }
        sourceIds.add(source.id);
        // `other` is deliberately conservative: without a URL it cannot be
        // audited as a source. Only operator context is allowed to be local.
        if (EXTERNAL_SOURCE_KINDS.has(source.kind) && !isAbsoluteHttpUrl(source.url)) {
            errors.push({ path: `sources.${index}.url`, message: 'External sources require an absolute http(s) URL.' });
        }
    });
    let hasUnavailableEvidence = false;
    deck.slides.forEach((slide, index) => {
        const slideHasUnavailableEvidence = visitEvidence(slide, `${slidePrefix}.${index}`, sourceIds, errors);
        hasUnavailableEvidence || (hasUnavailableEvidence = slideHasUnavailableEvidence);
    });
    if ((hasUnavailableEvidence || deck.meta.dataGaps.length > 0) &&
        !deck.slides.some((slide) => slide.layout === 'data-needed')) {
        errors.push({
            path: 'slides',
            message: 'A data-needed slide is required when evidence is unavailable or meta.dataGaps is non-empty.',
        });
    }
    return errors;
}
/** Validates a candidate slide and reports errors using tool-friendly field paths. */
function validateDeckSlide(input, index) {
    const parsed = deck_schema_1.deckSlideSchema.safeParse(input);
    const prefix = index === undefined ? '' : `slides.${index}`;
    if (!parsed.success)
        return { ok: false, errors: zodErrors(parsed.error, prefix) };
    const errors = [];
    // Source ids are resolved only in validateDeck, because a standalone slide
    // has no source registry. Per-slide validation still enforces local safety.
    visitEvidence(parsed.data, prefix || 'slides', undefined, errors);
    return errors.length ? { ok: false, errors } : { ok: true, data: parsed.data };
}
/** Validates the full document, including cross-slide and source-resolution rules. */
function validateDeck(input) {
    const parsed = deck_schema_1.deckV1Schema.safeParse(input);
    if (!parsed.success)
        return { ok: false, errors: zodErrors(parsed.error) };
    const errors = deckRuleErrors(parsed.data);
    return errors.length ? { ok: false, errors } : { ok: true, data: parsed.data };
}
